import {
  createColumnHelper,
  getCoreRowModel,
  type Row,
  useReactTable,
} from '@tanstack/react-table'
import { Copy } from 'lucide-react'
import { useMemo } from 'react'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import CopyButtonWithAnimation from 'components/reusable/CopyButtonWithAnimation'
import TanstackTable from 'components/reusable/table/TanstackTable'
import { Badge } from 'slab'
import { getLocaleDateString } from 'utils'
import { useGetBrokersMembersMemberIdFinancialAccounts } from 'utils/web_api'
import { type BrokersFinancialAccount } from 'utils/web_api.schemas'

import FinancialAccountActions from './FinancialAccountActions'
import StatusBadge from './StatusBadge'

type FinancialAccountsTableProps = {
  memberId: string
}

const columnHelper = createColumnHelper<BrokersFinancialAccount>()

const FinancialAccountsTable = ({
  memberId,
}: FinancialAccountsTableProps) => {
  const { data, isLoading, isError, error } =
    useGetBrokersMembersMemberIdFinancialAccounts(memberId, {
      query: {
        enabled: !!memberId,
        refetchOnWindowFocus: false,
      },
    })

  // The API returns an array of financial accounts
  const accounts = useMemo(() => {
    if (!data) return []
    return Array.isArray(data) ? data : [data]
  }, [data])

  const columns = useMemo(
    () => [
      columnHelper.display({
        header: 'Actions',
        cell: ({ row }: { row: Row<BrokersFinancialAccount> }) => (
          <FinancialAccountActions
            memberId={memberId}
            account={row.original}
          />
        ),
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: (info) => <StatusBadge status={info.getValue()} />,
      }),
      columnHelper.accessor('masked_account_number', {
        header: 'Account Number',
        cell: (info) => {
          const maskedAccountNumber = info.getValue()
          if (!maskedAccountNumber) return <span className='text-gray-400'>—</span>

          return (
            <code className='text-sm bg-gray-100 px-2 py-1 rounded'>
              {maskedAccountNumber}
            </code>
          )
        },
      }),
      columnHelper.accessor('enrollment_id', {
        header: 'Enrollment',
        cell: (info) => {
          const enrollmentId = info.getValue()
          if (!enrollmentId) return <span className='text-gray-400'>—</span>

          return (
            <Badge type='info'>
              {enrollmentId.slice(-6)}
            </Badge>
          )
        },
      }),
      columnHelper.accessor('remote_id', {
        header: 'Remote ID',
        cell: (info) => {
          const remoteId = info.getValue()
          if (!remoteId) return <span className='text-gray-400'>—</span>

          const truncated = remoteId.length > 12
            ? `${remoteId.slice(0, 8)}...${remoteId.slice(-4)}`
            : remoteId

          return (
            <div className='flex items-center'>
              <code className='text-sm bg-gray-100 px-2 py-1 rounded'>
                {truncated}
              </code>
              <CopyButtonWithAnimation content={remoteId}>
                <Copy size={16} className='text-gray-600' />
              </CopyButtonWithAnimation>
            </div>
          )
        },
      }),
      columnHelper.accessor('created_at', {
        header: 'Created',
        cell: (info) => getLocaleDateString(new Date(info.getValue())),
      }),
      columnHelper.accessor('updated_at', {
        header: 'Last Updated',
        cell: (info) => getLocaleDateString(new Date(info.getValue())),
      }),
    ],
    [memberId]
  )

  const table = useReactTable({
    data: accounts,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <TanstackTable
      table={table}
      isLoading={isLoading}
      isError={isError}
      errorMessage={(error as GrapeAPIErrorType)?.errors[0]}
      noDataMessage='No VBAN financial accounts found for this member.'
      paginationLabelSingular='account'
      tableMessageHeightClass='h-32'
    />
  )
}

export default FinancialAccountsTable
